import React, { useState } from "react";
import { Card, Flex, Text, Button, Badge } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { AuthSevenRoomsDialog } from "./AuthSevenRoomsDialog";
import { SevenRoomsChoiceDialog } from "./SevenRoomsChoiceDialog";
import { SevenRoomsMappingDialog } from "./SevenRoomsMappingDialog";
import { useValidateSevenRooms, useGetSevenRoomsStatus } from "hooks";
import { PermissionAlertDialog } from "components";
import { useToast } from "../../../context/ToastContext";

export function BrowseSevenRoomsCard({ userProfile }) {
  const { setToastMessage } = useToast();
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [environment, setEnvironment] = useState("demo");
  const [isClientIdValid, setIsClientIdValid] = useState(true);
  const [isClientSecretValid, setIsClientSecretValid] = useState(true);
  const [isEnvironmentValid, setIsEnvironmentValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [choiceDialogOpen, setChoiceDialogOpen] = useState(false);
  const [pendingChoiceData, setPendingChoiceData] = useState(null);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [venuesForMapping, setVenuesForMapping] = useState([]);
  const [initialMappings, setInitialMappings] = useState([]);
  const [initialFieldMappings, setInitialFieldMappings] = useState(null);
  const [initialPushEnabled, setInitialPushEnabled] = useState(true);
  const [initialPullEnabled, setInitialPullEnabled] = useState(false);
  const [initialLastPullTime, setInitialLastPullTime] = useState(null);
  const [initialClientTagGroup, setInitialClientTagGroup] = useState("");
  const [initialClientTagName, setInitialClientTagName] = useState("");
  const [reconnectAction, setReconnectAction] = useState(null);
  const validateSevenRooms = useValidateSevenRooms();
  const { data: sevenroomsStatusData } = useGetSevenRoomsStatus(userProfile.organisation_id);

  const isSevenRoomsActive = () => {
    return sevenroomsStatusData?.data?.is_active === true;
  };

  const resetFieldValidity = () => {
    setIsClientIdValid(true);
    setIsClientSecretValid(true);
    setIsEnvironmentValid(true);
  };

  const handleCancelAuthDialog = () => {
    setClientId("");
    setClientSecret("");
    setEnvironment("demo");
    resetFieldValidity();
  };

  const handleOpenChange = (isOpen) => {
    setOpenDialog(isOpen);
    if (!isOpen) {
      handleCancelAuthDialog();
    }
  };

  const handleConnectClick = () => {
    const isOwner = userProfile.user_role === "Owner";

    if (isOwner) {
      handleOpenChange(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  const openMappingStep = (
    venues,
    action = null,
    locationMappings = [],
    fieldMappings = null,
    pushEnabled = true,
    pullEnabled = false,
    lastPullTime = null,
    clientTagGroup = "",
    clientTagName = ""
  ) => {
    setVenuesForMapping(Array.isArray(venues) ? venues : []);
    setReconnectAction(action);
    setInitialMappings(Array.isArray(locationMappings) ? locationMappings : []);
    setInitialFieldMappings(fieldMappings);
    setInitialPushEnabled(Boolean(pushEnabled));
    setInitialPullEnabled(Boolean(pullEnabled));
    setInitialLastPullTime(lastPullTime || null);
    setInitialClientTagGroup(clientTagGroup || "");
    setInitialClientTagName(clientTagName || "");
    setOpenDialog(false);
    setChoiceDialogOpen(false);
    setMappingDialogOpen(true);
  };

  const handleSave = async (choice = null) => {
    const trimmedId = clientId.trim();
    const trimmedSecret = clientSecret.trim();
    let hasError = false;

    if (!trimmedId) {
      setIsClientIdValid(false);
      hasError = true;
    }
    if (!trimmedSecret) {
      setIsClientSecretValid(false);
      hasError = true;
    }
    if (!environment) {
      setIsEnvironmentValid(false);
      hasError = true;
    }
    if (hasError) {
      return;
    }

    setIsLoading(true);
    resetFieldValidity();

    try {
      const payload = {
        client_id: trimmedId,
        client_secret: trimmedSecret,
        environment,
        organisation_id: userProfile.organisation_id,
      };

      if (choice) payload.action_choice = choice;

      const result = await validateSevenRooms.mutateAsync(payload);
      const status = result?.data?.status;

      if (status === "pending_user_choice") {
        setPendingChoiceData(result.data);
        setChoiceDialogOpen(true);
        return;
      }

      if (status === "success" || status === "restored" || status === "resumed") {
        queryClient.invalidateQueries(["sevenrooms-status"]);
        queryClient.invalidateQueries(["sevenrooms-mappings", userProfile.organisation_id]);
        openMappingStep(
          result.data.venues,
          result.data.action || null,
          result.data.location_mappings || [],
          result.data.field_mappings || null,
          result.data.push_enabled,
          result.data.pull_enabled,
          result.data.last_pull_time,
          result.data.client_tag_group,
          result.data.client_tag_name
        );
        return;
      }

      if (result?.data?.invalid === "credentials" || result?.data?.invalid === "environment") {
        markInvalidField(result.data.invalid);
      }
    } catch (error) {
      console.error("Validation error:", error);
      const invalidField = error?.response?.data?.invalid;
      if (invalidField === "credentials" || invalidField === "environment") {
        markInvalidField(invalidField);
      } else {
        setToastMessage("Unexpected error. Please try again.");
        resetFieldValidity();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const markInvalidField = (invalidField) => {
    if (invalidField === "environment") {
      setIsEnvironmentValid(false);
      return;
    }
    setIsClientIdValid(false);
    setIsClientSecretValid(false);
  };

  return (
    <>
      <Card
        style={{
          display: "flex",
          minWidth: "455px",
          padding: "var(--space-4)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Flex align="center" gap="4">
          <Flex
            height="40px"
            width="40px"
            p="2"
            style={{
              borderRadius: "var(--space-1)",
              background: "#FFFFFF",
              border: "1px solid var(--gray-a6)",
            }}
          >
            <img
              src={require("../../../assets/images/sevenrooms.jpeg")}
              alt="SevenRooms"
              style={{ width: "24px" }}
            />
          </Flex>
          <Flex direction="column" justify="center" align="start">
            <Text size="4" weight="medium">
              SevenRooms
            </Text>
            <Text size="1" color="gray">
              Reservations CRM
            </Text>
          </Flex>
        </Flex>

        <Flex align="center">
          {isSevenRoomsActive() ? (
            <Badge size="1" variant="soft" color="green">
              Connected
            </Badge>
          ) : (
            <Button onClick={handleConnectClick} size="2" variant="soft" color="gray">
              Connect
            </Button>
          )}
        </Flex>
      </Card>

      <AuthSevenRoomsDialog
        isOpen={openDialog}
        onOpenChange={handleOpenChange}
        onCancel={handleCancelAuthDialog}
        onSave={handleSave}
        clientId={clientId}
        clientSecret={clientSecret}
        environment={environment}
        onClientIdChange={(event) => {
          setClientId(event.target.value);
          setIsClientIdValid(true);
        }}
        onClientSecretChange={(event) => {
          setClientSecret(event.target.value);
          setIsClientSecretValid(true);
        }}
        onEnvironmentChange={(value) => {
          setEnvironment(value);
          setIsEnvironmentValid(true);
        }}
        isClientIdValid={isClientIdValid}
        isClientSecretValid={isClientSecretValid}
        isEnvironmentValid={isEnvironmentValid}
        isLoading={isLoading}
      />

      <SevenRoomsChoiceDialog
        isOpen={choiceDialogOpen}
        onOpenChange={setChoiceDialogOpen}
        data={pendingChoiceData}
        onChoose={(choice) => {
          setChoiceDialogOpen(false);
          handleSave(choice);
        }}
      />

      <SevenRoomsMappingDialog
        isOpen={mappingDialogOpen}
        onOpenChange={setMappingDialogOpen}
        venues={venuesForMapping}
        initialMappings={initialMappings}
        initialFieldMappings={initialFieldMappings}
        initialPushEnabled={initialPushEnabled}
        initialPullEnabled={initialPullEnabled}
        initialLastPullTime={initialLastPullTime}
        initialClientTagGroup={initialClientTagGroup}
        initialClientTagName={initialClientTagName}
        reconnectAction={reconnectAction}
      />

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
